import React, { useRef, useEffect, useState } from 'react';
import {
    Box,
    CircularProgress,
    Avatar,
    Paper,
    Typography,
    TextField,
    IconButton,
    Chip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github.css';

interface Message {
    sender: 'user' | 'bot';
    content: string;
}

interface ChatAreaProps {
    messages: Message[];
    input: string;
    setInput: (value: string) => void;
    handleSendMessage: () => void;
    isSendingMessage: boolean;
    isFetchingMessages: boolean;
    selectedChatId: string | null;
}

const ChatArea: React.FC<ChatAreaProps> = ({
                                               messages,
                                               input,
                                               setInput,
                                               handleSendMessage,
                                               isSendingMessage,
                                               isFetchingMessages,
                                               selectedChatId,
                                           }) => {
    const messageEndRef = useRef<HTMLDivElement | null>(null);

    // Loader messages
    const loadingMessages = [
        '🔄 Retrieving Data...',
        '🔎 Analyzing Data...',
        '📝 Generating Response...',
    ];
    const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);

    // Predefined prompts
    const predefinedPrompts = [
        'What is happening in the market today?',
        'Give me the analysis of fundamentals and technicals on TSLA.',
        'What are the latest market trends?',
        'Provide a technical analysis for AAPL.',
        'Explain the fundamental analysis of AMZN.',
        'What are the top performing sectors today?',
        'How is the cryptocurrency market performing?',
        'Give me a summary of today\'s economic news.',
        'What are the key earnings reports this week?',
        'What is your outlook on the US stock market?',
        'Analyze the risk factors for investing in TSLA.',
        'Compare the P/E ratios of major tech companies.',
        'What are the implications of the latest Fed meeting?',
        'How do geopolitical events affect the oil market?',
        'Provide investment recommendations for a balanced portfolio.',
    ];
    const [suggestedPrompts, setSuggestedPrompts] = useState<string[]>([]);

    useEffect(() => {
        // Select 3 random prompts
        const shuffledPrompts = predefinedPrompts.sort(() => 0.5 - Math.random());
        setSuggestedPrompts(shuffledPrompts.slice(0, 3));
    }, [messages]); // Update suggestions after each message

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (isSendingMessage) {
            timer = setInterval(() => {
                setLoadingMessageIndex(
                    (prevIndex) => (prevIndex + 1) % loadingMessages.length
                );
            }, 3000); // Change message every 3 seconds
        } else {
            setLoadingMessageIndex(0); // Reset when not sending a message
        }
        return () => {
            clearInterval(timer);
        };
    }, [isSendingMessage]);

    useEffect(() => {
        messageEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isSendingMessage]);

    const handlePromptClick = (prompt: string) => {
        setInput(prompt);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Messages Display */}
            <Box
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    minHeight: 0,
                    p: 2,
                    mb: 2,
                    backgroundColor: 'background.default',
                    borderRadius: 2,
                    boxShadow: 1,
                    position: 'relative',
                }}
            >
                {isFetchingMessages ? (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {messages.length === 0 && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="h6">Need some ideas?</Typography>
                                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {suggestedPrompts.map((prompt, index) => (
                                        <Chip
                                            key={index}
                                            label={prompt}
                                            onClick={() => handlePromptClick(prompt)}
                                            sx={{ cursor: 'pointer' }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                        {messages.map((msg, index) => (
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    justifyContent:
                                        msg.sender === 'user' ? 'flex-end' : 'flex-start',
                                    mb: 2,
                                }}
                            >
                                {msg.sender === 'bot' ? (
                                    <>
                                        <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                                            <SmartToyIcon />
                                        </Avatar>
                                        <Paper
                                            elevation={3}
                                            sx={{
                                                maxWidth: '90%',
                                                p: 1.5,
                                                borderRadius: 2,
                                                backgroundColor: 'primary.main',
                                                color: 'primary.contrastText',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                rehypePlugins={[rehypeHighlight]}
                                            >
                                                {msg.content}
                                            </ReactMarkdown>
                                        </Paper>
                                    </>
                                ) : (
                                    <>
                                        <Paper
                                            elevation={3}
                                            sx={{
                                                maxWidth: '90%',
                                                p: 1.5,
                                                borderRadius: 2,
                                                backgroundColor: 'secondary.main',
                                                color: 'text.primary',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            <Typography variant="body1">{msg.content}</Typography>
                                        </Paper>
                                        <Avatar sx={{ bgcolor: 'secondary.main', ml: 1 }}>
                                            <PersonIcon />
                                        </Avatar>
                                    </>
                                )}
                            </Box>
                        ))}
                        {isSendingMessage && !isFetchingMessages && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    mt: 2,
                                    textAlign: 'center',
                                }}
                            >
                                <CircularProgress />
                                <Typography variant="h6" sx={{ mt: 2 }}>
                                    {loadingMessages[loadingMessageIndex]}
                                </Typography>
                            </Box>
                        )}
                    </>
                )}
                <div ref={messageEndRef} />
            </Box>

            {/* Message Input */}
            <Box
                component="form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSendMessage();
                }}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: 'background.paper',
                    borderRadius: 2,
                    p: 1,
                    boxShadow: 2,
                }}
            >
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Type your message..."
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    multiline
                    minRows={1}
                    maxRows={4}
                    disabled={isSendingMessage || isFetchingMessages}
                    InputProps={{
                        sx: {
                            backgroundColor: 'background.paper',
                            borderRadius: 1,
                            paddingRight: '8px',
                            color: 'text.primary',
                        },
                    }}
                    sx={{
                        mr: 1,
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                        }
                    }}
                />
                <IconButton
                    color="primary"
                    type="submit"
                    disabled={
                        isSendingMessage ||
                        !input.trim() ||
                        isFetchingMessages
                    }
                    aria-label="send message"
                >
                    {isSendingMessage ? <CircularProgress size={24} /> : <SendIcon />}
                </IconButton>
            </Box>
        </Box>
    );
};

export default ChatArea;
